import { useState } from 'react';
import { PlusIcon, Trash2Icon, TriangleAlertIcon } from 'lucide-react';

import type { PersonaRow, ScoringConfig, ScoringConfigFields, ConfigOptionSpec } from '@/lib/api';
import { useContent, useOptionalT, useT } from '@/lib/i18n';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { bandCoverageIssues, stripSegments, TRACK, type BandRow } from './bands';

/**
 * THE STRUCTURED SCORING-CONFIG EDITOR — one form, driven by what the plugin
 * declares (T5's remaining half, PROGRESS §25 carry-forward).
 *
 * ### Why this is one generic form and not three model-shaped ones
 *
 * The carry-forward's reason for deferring these editors was that they *"differ
 * per model"*. That is true and it is the argument AGAINST writing them by hand:
 * three bespoke forms is `if (model === 'RIASEC')` expressed as a directory, and
 * the extension story this platform sells — a fourth model is one plugin file
 * and zero UI edits — would have been paid for in the one screen that exists to
 * use it. `harness-viz.test.tsx` would have caught the conditional; it could not
 * have caught three files.
 *
 * So the plugin describes its own config surface (`ScoringPlugin.configFields`)
 * and this renders whatever it is handed. No model is named here, and a model
 * that declares nothing gets the raw-JSON tab it has always had.
 *
 * ### It is a VIEW OVER THE DRAFT, not a second editor
 *
 * Every change calls `onChange` with a whole new config, and the parent holds
 * exactly one draft that both this and the JSON textarea render. That is what
 * keeps the preview gate honest: editing here clears the preview and re-arms
 * `Preview changes` in the same way typing JSON does, and the server's
 * `validateConfig` remains the only thing that decides whether a config is
 * acceptable. Nothing here is a second opinion on that — the inline checks are
 * about telling an operator now rather than after a round trip.
 *
 * ### `version` is not on this form
 *
 * It is derived server-side and bumps only on a scoring-semantic change. A field
 * for it would be a field whose value the server discards.
 */

/* ------------------------------------------------------------ small helpers */

/** A section with a heading, so the form reads as a document rather than a wall. */
function Section({
  title,
  hint,
  children,
  action,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <section className="space-y-3 border-t pt-4 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium">{title}</h3>
          {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/**
 * A number field that can be genuinely EMPTY.
 *
 * `<Input type="number">` bound straight to a number cannot hold the
 * intermediate states of typing one — clearing the box to retype it would read
 * as `NaN`, and coercing `NaN` to 0 silently writes a real value the operator
 * never chose. So the text is local state and `onChange` fires only for a value
 * that parses, with `onEmpty` as the separate, explicit answer to "the field is
 * now blank".
 */
function NumberField({
  value,
  onChange,
  onEmpty,
  ...rest
}: {
  value: number | undefined;
  onChange: (next: number) => void;
  onEmpty?: () => void;
  min?: number;
  max?: number;
  step?: number;
  'aria-label': string;
  className?: string;
  id?: string;
}) {
  const [text, setText] = useState<string | null>(null);
  const shown = text ?? (value === undefined ? '' : String(value));

  return (
    <Input
      type="number"
      inputMode="decimal"
      value={shown}
      // Numbers are LTR even in an Arabic console — a minus sign or a decimal
      // point reordering around a bidi-neutral field is the `usePercent` bug in
      // an input box.
      dir="ltr"
      onChange={(e) => {
        const next = e.target.value;
        setText(next);
        if (next.trim() === '') {
          onEmpty?.();
          return;
        }
        const parsed = Number(next);
        if (Number.isFinite(parsed)) onChange(parsed);
      }}
      onBlur={() => setText(null)}
      {...rest}
    />
  );
}

/* --------------------------------------------------------- dimensions + labels */

function DimensionsSection({
  config,
  fields,
  onChange,
}: {
  config: ScoringConfig;
  fields: ScoringConfigFields;
  onChange: (next: ScoringConfig) => void;
}) {
  const t = useT();
  const editable = fields.dimensions.editable;

  const setLabel = (key: string, label: string) =>
    onChange({ ...config, dimensionLabels: { ...config.dimensionLabels, [key]: label } });

  const rename = (from: string, to: string) => {
    if (!to || to === from || config.dimensions.includes(to)) return;
    const labels = { ...config.dimensionLabels };
    labels[to] = labels[from] ?? to;
    delete labels[from];
    onChange({
      ...config,
      dimensions: config.dimensions.map((d) => (d === from ? to : d)),
      dimensionLabels: labels,
      // A renamed dimension takes its norms with it, or the table silently
      // stops applying to anything and the percentiles vanish with no edit
      // having touched them.
      ...(config.norms
        ? {
            norms: Object.fromEntries(
              Object.entries(config.norms).map(([persona, table]) => [
                persona,
                Object.fromEntries(
                  Object.entries(table).map(([dim, stat]) => [dim === from ? to : dim, stat]),
                ),
              ]),
            ),
          }
        : {}),
    });
  };

  const remove = (key: string) => {
    const labels = { ...config.dimensionLabels };
    delete labels[key];
    onChange({
      ...config,
      dimensions: config.dimensions.filter((d) => d !== key),
      dimensionLabels: labels,
      ...(config.norms
        ? {
            norms: Object.fromEntries(
              Object.entries(config.norms).map(([persona, table]) => {
                const next = { ...table };
                delete next[key];
                return [persona, next];
              }),
            ),
          }
        : {}),
    });
  };

  const add = () => {
    // A placeholder key rather than a blank one: an empty string is a valid
    // object key and would collide with the next blank row.
    let key = 'new_dimension';
    let n = 2;
    while (config.dimensions.includes(key)) key = `new_dimension_${n++}`;
    onChange({
      ...config,
      dimensions: [...config.dimensions, key],
      dimensionLabels: { ...config.dimensionLabels, [key]: '' },
    });
  };

  return (
    <Section
      title={t('configForm.dimensions')}
      hint={editable ? t('configForm.dimensions.hint') : t('configForm.dimensions.fixed')}
      action={
        editable && (
          <Button type="button" size="sm" variant="outline" onClick={add}>
            <PlusIcon className="size-4" />
            {t('configForm.dimensions.add')}
          </Button>
        )
      }
    >
      <div className="space-y-2">
        {config.dimensions.map((key) => (
          <div key={key} className="flex flex-wrap items-center gap-2">
            {editable ? (
              <Input
                value={key}
                dir="ltr"
                className="w-48 font-mono text-xs"
                aria-label={t('configForm.dimensions.key')}
                onChange={(e) => rename(key, e.target.value)}
              />
            ) : (
              // Fixed keys render as what they are — an identifier, not a
              // disabled input inviting an edit that will not take.
              <Badge variant="outline" className="w-48 justify-start font-mono" dir="ltr">
                {key}
              </Badge>
            )}
            <Input
              value={config.dimensionLabels[key] ?? ''}
              className="min-w-40 flex-1"
              aria-label={t('configForm.dimensions.label', { key })}
              placeholder={key}
              onChange={(e) => setLabel(key, e.target.value)}
            />
            {editable && (
              <Button
                type="button"
                size="icon"
                variant="ghost"
                aria-label={t('configForm.dimensions.remove', { key })}
                onClick={() => remove(key)}
              >
                <Trash2Icon className="size-4" />
              </Button>
            )}
          </div>
        ))}
      </div>

      {/* §33b — one label, English, and the console localises at display time.
          Said here because the field looks bilingual-shaped and is not, and an
          operator typing Arabic into it would be publishing Arabic on `/v1`. */}
      <p className="text-muted-foreground text-xs">{t('configForm.dimensions.english')}</p>
    </Section>
  );
}

/* ---------------------------------------------------------------- the bands */

/**
 * The 0–100 strip, drawn from the same segments the validator reasons about.
 *
 * The strip is the reason this section exists as a form rather than as three
 * more number boxes: a gap between two bands is nearly invisible as digits and
 * unmissable as a hole in a bar. Uncovered stretches are painted as a hazard AND
 * named in the issues list below — status is never colour alone.
 */
function BandStrip({ bands }: { bands: readonly BandRow[] }) {
  const t = useT();
  const segments = stripSegments(bands);

  return (
    <div
      className="border-border flex h-8 w-full overflow-hidden rounded-md border"
      role="img"
      aria-label={t('configForm.bands.strip')}
      dir="ltr"
    >
      {segments.map((segment, i) => {
        const width = ((segment.to - segment.from) / TRACK) * 100;
        if (width <= 0) return null;
        return (
          <div
            key={`${segment.from}-${segment.to}-${i}`}
            style={{ width: `${width}%` }}
            className={
              segment.band
                ? 'bg-muted text-muted-foreground flex items-center justify-center overflow-hidden border-e text-[10px] whitespace-nowrap'
                : 'bg-destructive/20 border-destructive/50 flex items-center justify-center border-e border-dashed'
            }
            title={
              segment.band
                ? `${segment.band.label} · ${segment.band.min}–${segment.band.max}`
                : t('configForm.bands.uncovered', { from: segment.from, to: segment.to - 1 })
            }
          >
            {segment.band?.label}
          </div>
        );
      })}
    </div>
  );
}

function BandsSection({
  config,
  fields,
  onChange,
}: {
  config: ScoringConfig;
  fields: ScoringConfigFields;
  onChange: (next: ScoringConfig) => void;
}) {
  const t = useT();
  const allowed = fields.bands.labels;
  const issues = bandCoverageIssues(config.bands);

  const patch = (index: number, change: Partial<BandRow>) =>
    onChange({
      ...config,
      bands: config.bands.map((band, i) => (i === index ? { ...band, ...change } : band)),
    });

  const add = () => {
    // Starts where the current bands stop, so the common case — adding a band
    // on the end — lands contiguous rather than immediately warning.
    const ceiling = config.bands.length > 0 ? Math.max(...config.bands.map((b) => b.max)) : -1;
    const min = Math.min(ceiling + 1, 100);
    onChange({
      ...config,
      bands: [...config.bands, { min, max: 100, label: allowed?.[0] ?? '' }],
    });
  };

  return (
    <Section
      title={t('configForm.bands')}
      hint={t('configForm.bands.hint')}
      action={
        <Button type="button" size="sm" variant="outline" onClick={add}>
          <PlusIcon className="size-4" />
          {t('configForm.bands.add')}
        </Button>
      }
    >
      <BandStrip bands={config.bands} />

      <div className="space-y-2">
        {config.bands.map((band, index) => (
          <div key={index} className="flex flex-wrap items-center gap-2">
            {allowed ? (
              <Select value={band.label} onValueChange={(label) => patch(index, { label })}>
                <SelectTrigger
                  className="min-w-40 flex-1"
                  aria-label={t('configForm.bands.label', { index: index + 1 })}
                >
                  <SelectValue placeholder={t('configForm.bands.pickLabel')} />
                </SelectTrigger>
                <SelectContent>
                  {allowed.map((label) => (
                    <SelectItem key={label} value={label}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={band.label}
                className="min-w-40 flex-1"
                aria-label={t('configForm.bands.label', { index: index + 1 })}
                onChange={(e) => patch(index, { label: e.target.value })}
              />
            )}
            <NumberField
              value={band.min}
              min={0}
              max={100}
              className="w-24"
              aria-label={t('configForm.bands.min', { index: index + 1 })}
              onChange={(min) => patch(index, { min })}
            />
            <span className="text-muted-foreground text-xs">–</span>
            <NumberField
              value={band.max}
              min={0}
              max={100}
              className="w-24"
              aria-label={t('configForm.bands.max', { index: index + 1 })}
              onChange={(max) => patch(index, { max })}
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              aria-label={t('configForm.bands.remove', { index: index + 1 })}
              onClick={() =>
                onChange({ ...config, bands: config.bands.filter((_, i) => i !== index) })
              }
            >
              <Trash2Icon className="size-4" />
            </Button>
          </div>
        ))}
      </div>

      {/* The same sentences the server would answer with, said before the round
          trip rather than instead of it — `bandCoverageIssues` is mirrored from
          the contract and pinned to it by a test. */}
      {issues.length > 0 && (
        <Alert variant="destructive" role="alert">
          <TriangleAlertIcon className="size-4" />
          <AlertDescription>
            <p className="mb-1 font-medium">{t('configForm.bands.issues')}</p>
            <ul className="list-inside list-disc">
              {issues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Inclusive at both ends, which is why 0–39 then 40–69 is contiguous and
          0–39 then 39–69 is an overlap. Nobody guesses this correctly. */}
      <p className="text-muted-foreground text-xs">{t('configForm.bands.inclusive')}</p>
    </Section>
  );
}

/* ---------------------------------------------------------------- the norms */

/**
 * THE NORM TABLES — the section that turns a score into something interpretable.
 *
 * `ScoringConfig.norms[persona][dimension] = {mean, sd}` has been in the
 * contract since Phase 2 and Big Five has read it since N3, but nothing could
 * author it: every module in the platform runs with no norms, so every
 * percentile is absent and every result is a raw 0–100 with no reference group.
 * That is the gap this section closes, and it is why the norms editor came
 * first — the charts that read percentiles have nothing to draw without it.
 *
 * ### One table per persona, added deliberately
 *
 * Norms are keyed by persona because a 62 means different things for a school
 * leaver and a mid-career changer. A grid of every persona × every dimension
 * would be mostly empty and would present authoring a norm as the default state
 * rather than as a calibration somebody did. So a table is added for a named
 * persona, and a persona with no table simply has none — which the result view
 * already handles by showing the band alone (D-PP2).
 *
 * ### An emptied cell REMOVES the norm
 *
 * Not "sets it to zero". A blank mean is the operator saying this dimension is
 * not calibrated for this cohort, and the honest encoding of that is an absent
 * entry — the same distinction the envelope draws when it omits `percentile`
 * rather than sending 0.
 */
function NormsSection({
  config,
  personas,
  onChange,
}: {
  config: ScoringConfig;
  personas: PersonaRow[];
  onChange: (next: ScoringConfig) => void;
}) {
  const t = useT();
  const content = useContent();
  const [adding, setAdding] = useState('');

  const norms = config.norms ?? {};
  const withTables = Object.keys(norms);
  const available = personas.filter((p) => !withTables.includes(p.personaId));
  const personaName = (id: string) => {
    const match = personas.find((p) => p.personaId === id);
    return match ? content(match.name) : id;
  };

  const write = (next: ScoringConfig['norms']) => {
    // An empty `norms` object is dropped entirely rather than saved as `{}`:
    // the field is optional in the contract, and `{}` and absent would produce
    // an ever-so-slightly different config fingerprint for no difference in
    // behaviour — which is a version bump for nothing.
    const cleaned = Object.fromEntries(
      Object.entries(next ?? {}).filter(([, table]) => Object.keys(table).length > 0),
    );
    const { norms: _dropped, ...rest } = config;
    onChange(Object.keys(cleaned).length > 0 ? { ...rest, norms: cleaned } : rest);
  };

  const setStat = (persona: string, dimension: string, change: Partial<{ mean: number; sd: number }>) => {
    const table = { ...(norms[persona] ?? {}) };
    const current = table[dimension] ?? { mean: 50, sd: 15 };
    table[dimension] = { ...current, ...change };
    write({ ...norms, [persona]: table });
  };

  const clearStat = (persona: string, dimension: string) => {
    const table = { ...(norms[persona] ?? {}) };
    delete table[dimension];
    write({ ...norms, [persona]: table });
  };

  return (
    <Section
      title={t('configForm.norms')}
      hint={t('configForm.norms.hint')}
      action={
        available.length > 0 && (
          <div className="flex items-center gap-2">
            <Select value={adding} onValueChange={setAdding}>
              <SelectTrigger className="w-56" aria-label={t('configForm.norms.persona')}>
                <SelectValue placeholder={t('configForm.norms.pickPersona')} />
              </SelectTrigger>
              <SelectContent>
                {available.map((persona) => (
                  <SelectItem key={persona.personaId} value={persona.personaId}>
                    {content(persona.name)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!adding}
              onClick={() => {
                // Seeded across every dimension at mean 50 / sd 15, which is a
                // STARTING SHAPE and is labelled as one — the same status
                // `configTemplate` has. Leaving the table empty would mean a
                // freshly added persona had a norm group and no norms, which is
                // the one state that reads as "calibrated" while being blank.
                const table = Object.fromEntries(
                  config.dimensions.map((d) => [d, { mean: 50, sd: 15 }]),
                );
                write({ ...norms, [adding]: table });
                setAdding('');
              }}
            >
              <PlusIcon className="size-4" />
              {t('configForm.norms.add')}
            </Button>
          </div>
        )
      }
    >
      {withTables.length === 0 ? (
        <Alert>
          <AlertDescription>{t('configForm.norms.none')}</AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-4">
          {withTables.map((persona) => (
            <div key={persona} className="rounded-md border p-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-medium">{personaName(persona)}</p>
                  <p className="text-muted-foreground font-mono text-xs" dir="ltr">{persona}</p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    const next = { ...norms };
                    delete next[persona];
                    write(next);
                  }}
                >
                  <Trash2Icon className="size-4" />
                  {t('configForm.norms.removeTable')}
                </Button>
              </div>

              <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto_auto] sm:items-center">
                <span className="text-muted-foreground text-xs">{t('configForm.norms.dimension')}</span>
                <span className="text-muted-foreground w-24 text-xs">{t('configForm.norms.mean')}</span>
                <span className="text-muted-foreground w-24 text-xs">{t('configForm.norms.sd')}</span>
                {/* The delete column has no header — every button in it carries
                    its own `aria-label` naming the dimension it clears. */}
                <span aria-hidden />

                {config.dimensions.map((dimension) => {
                  const stat = norms[persona]?.[dimension];
                  return (
                    <ConfigNormRow
                      key={dimension}
                      dimension={dimension}
                      label={config.dimensionLabels[dimension] ?? dimension}
                      stat={stat}
                      onMean={(mean) => setStat(persona, dimension, { mean })}
                      onSd={(sd) => setStat(persona, dimension, { sd })}
                      onClear={() => clearStat(persona, dimension)}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-muted-foreground text-xs">{t('configForm.norms.scale')}</p>
    </Section>
  );
}

function ConfigNormRow({
  dimension,
  label,
  stat,
  onMean,
  onSd,
  onClear,
}: {
  dimension: string;
  label: string;
  stat: { mean: number; sd: number } | undefined;
  onMean: (mean: number) => void;
  onSd: (sd: number) => void;
  onClear: () => void;
}) {
  const t = useT();
  // `sd` must be positive in the contract (`NormStat`), and a zero would make
  // the z-score infinite rather than merely wrong. Said on the row that caused
  // it rather than in the summary at the bottom.
  const badSd = stat !== undefined && !(stat.sd > 0);

  return (
    <>
      <span className="text-sm">
        {label}
        <span className="text-muted-foreground ms-2 font-mono text-xs" dir="ltr">{dimension}</span>
        {badSd && (
          <span className="text-destructive ms-2 text-xs">{t('configForm.norms.sdPositive')}</span>
        )}
      </span>
      <NumberField
        value={stat?.mean}
        className="w-24"
        aria-label={t('configForm.norms.meanFor', { dimension: label })}
        onChange={onMean}
        onEmpty={onClear}
      />
      <NumberField
        value={stat?.sd}
        min={0}
        className="w-24"
        aria-label={t('configForm.norms.sdFor', { dimension: label })}
        onChange={onSd}
        onEmpty={onClear}
      />
      <Button
        type="button"
        size="icon"
        variant="ghost"
        aria-label={t('configForm.norms.clear', { dimension: label })}
        onClick={onClear}
        disabled={stat === undefined}
      >
        <Trash2Icon className="size-4" />
      </Button>
    </>
  );
}

/* --------------------------------------------------- completion and QC gates */

function GatesSection({
  config,
  onChange,
}: {
  config: ScoringConfig;
  onChange: (next: ScoringConfig) => void;
}) {
  const t = useT();

  return (
    <Section title={t('configForm.gates')} hint={t('configForm.gates.hint')}>
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor="cfg-minCompletion">{t('configForm.minCompletion')}</Label>
          <div className="flex items-center gap-2">
            <NumberField
              id="cfg-minCompletion"
              value={config.minCompletion}
              min={0}
              max={100}
              aria-label={t('configForm.minCompletion')}
              onChange={(minCompletion) => onChange({ ...config, minCompletion })}
            />
            {/* The unit, spelled out. This field is 0–100 in the contract and
                has been written as `0.61` in a payload at least once — which is
                valid, means 0.61%, and gates nothing. A suffix is cheaper than
                the bug. */}
            <span className="text-muted-foreground text-sm">%</span>
          </div>
          <p className="text-muted-foreground text-xs">{t('configForm.minCompletion.hint')}</p>
        </div>

        <div className="space-y-1">
          <Label htmlFor="cfg-straightLine">{t('configForm.straightLine')}</Label>
          <NumberField
            id="cfg-straightLine"
            value={config.qcThresholds.straightLineThreshold}
            min={0}
            max={1}
            step={0.05}
            aria-label={t('configForm.straightLine')}
            onChange={(straightLineThreshold) =>
              onChange({
                ...config,
                qcThresholds: { ...config.qcThresholds, straightLineThreshold },
              })
            }
          />
          {/* A PROPORTION, not a percentage — 0.8, not 80. The neighbouring
              field is 0–100 and this one is 0–1, which is exactly the pair a
              form has to label or somebody will type 80 here and flag nothing
              ever again. */}
          <p className="text-muted-foreground text-xs">{t('configForm.straightLine.hint')}</p>
        </div>

        <div className="space-y-1">
          <Label htmlFor="cfg-fastMs">{t('configForm.fastMs')}</Label>
          <NumberField
            id="cfg-fastMs"
            value={config.qcThresholds.fastMs}
            min={1}
            step={100}
            aria-label={t('configForm.fastMs')}
            onChange={(fastMs) =>
              onChange({ ...config, qcThresholds: { ...config.qcThresholds, fastMs } })
            }
          />
          <p className="text-muted-foreground text-xs">{t('configForm.fastMs.hint')}</p>
        </div>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------- model-specific knobs */

/**
 * `ScoringConfig.options` is `Record<string, unknown>` — a deliberately open bag
 * each plugin reads what it needs from. A form over an open bag is only possible
 * because the plugin says what is in it; a model that declares no options gets
 * no section, and its bag stays editable in the JSON tab.
 */
function OptionsSection({
  config,
  specs,
  onChange,
}: {
  config: ScoringConfig;
  specs: ConfigOptionSpec[];
  onChange: (next: ScoringConfig) => void;
}) {
  const t = useT();
  const optionalT = useOptionalT();

  const set = (key: string, value: unknown) =>
    onChange({ ...config, options: { ...config.options, [key]: value } });

  return (
    <Section title={t('configForm.options')} hint={t('configForm.options.hint')}>
      <div className="space-y-3">
        {specs.map((spec) => {
          // Catalogue first, then the plugin's own English — `useDimensionLabel`'s
          // order, and what makes a knob declared by a model this console has
          // never seen still render with a name rather than a key.
          const label = optionalT(`config.option.${spec.key}`) ?? spec.label;
          const value = config.options[spec.key];

          return (
            <div key={spec.key} className="flex flex-wrap items-center gap-3">
              <Label htmlFor={`cfg-opt-${spec.key}`} className="min-w-40">{label}</Label>

              {spec.kind === 'boolean' && (
                <Switch
                  id={`cfg-opt-${spec.key}`}
                  checked={value === true}
                  onCheckedChange={(checked) => set(spec.key, checked)}
                />
              )}

              {spec.kind === 'number' && (
                <NumberField
                  id={`cfg-opt-${spec.key}`}
                  value={typeof value === 'number' ? value : undefined}
                  {...(spec.min === undefined ? {} : { min: spec.min })}
                  {...(spec.max === undefined ? {} : { max: spec.max })}
                  {...(spec.step === undefined ? {} : { step: spec.step })}
                  className="w-32"
                  aria-label={label}
                  onChange={(next) => set(spec.key, next)}
                />
              )}

              {spec.kind === 'select' && (
                <Select
                  value={typeof value === 'string' ? value : ''}
                  onValueChange={(next) => set(spec.key, next)}
                >
                  <SelectTrigger className="w-56" aria-label={label}>
                    <SelectValue placeholder={t('configForm.options.pick')} />
                  </SelectTrigger>
                  <SelectContent>
                    {(spec.choices ?? []).map((choice) => (
                      <SelectItem key={choice} value={choice}>{choice}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {spec.help && <p className="text-muted-foreground text-xs">{spec.help}</p>}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

/* ------------------------------------------------------------------- the form */

export function ConfigForm({
  config,
  fields,
  personas,
  onChange,
}: {
  config: ScoringConfig;
  fields: ScoringConfigFields;
  personas: PersonaRow[];
  onChange: (next: ScoringConfig) => void;
}) {
  return (
    <div className="space-y-5">
      <DimensionsSection config={config} fields={fields} onChange={onChange} />
      <BandsSection config={config} fields={fields} onChange={onChange} />
      {/* Hidden entirely when the model does not read norms — see the flag's
          comment in the contract. An editor for a table nothing consults is the
          one control that cannot report its own uselessness. */}
      {fields.norms.supported && (
        <NormsSection config={config} personas={personas} onChange={onChange} />
      )}
      <GatesSection config={config} onChange={onChange} />
      {fields.options.length > 0 && (
        <OptionsSection config={config} specs={fields.options} onChange={onChange} />
      )}
    </div>
  );
}
